import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    LayoutDashboard,
    Sparkles,
    MessageSquare,
    User,
    Search,
    ClipboardList,
    UploadCloud,
    Settings,
    LogOut
} from 'lucide-react';
import { CANDIDATE_ROUTES } from '../../constants';
import { SETTINGS_ROUTES } from '../../../shared/settings/constants';
import { ConfirmDialog } from '../../../shared/components';
import { applicationApiService } from '../../services/applicationApi';
import Sidebar, { SidebarMenuItem } from '../../../shared/components/navigation/Sidebar';

interface CandidateSidebarProps {
    isCollapsed?: boolean;
    onToggle?: () => void;
    isMobileOpen?: boolean;
    onMobileClose?: () => void;
}

const CandidateSidebar: React.FC<CandidateSidebarProps> = ({
    isCollapsed = false,
    onToggle,
    isMobileOpen = false,
    onMobileClose
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [showLogoutDialog, setShowLogoutDialog] = useState(false);
    const [applicationsCount, setApplicationsCount] = useState<number>(0);

    const handleLogoutClick = () => {
        setShowLogoutDialog(true);
    };

    const handleLogoutConfirm = () => {
        localStorage.clear();
        sessionStorage.clear();
        navigate('/auth/login');
        setShowLogoutDialog(false);
    };

    const handleLogoutCancel = () => {
        setShowLogoutDialog(false);
    };

    useEffect(() => {
        const fetchApplicationsCount = async () => {
            try {
                const response = await applicationApiService.getStatistics();
                const total = response.payload?.total || 0;
                setApplicationsCount(total);
            } catch (error) {
                console.error('Failed to fetch applications count:', error);
                setApplicationsCount(0);
            }
        };

        fetchApplicationsCount();
    }, []);

    const menuItems: SidebarMenuItem[] = [
        {
            icon: (active: boolean) => <LayoutDashboard size={20} strokeWidth={active ? 2.5 : 2} />,
            label: 'Dashboard',
            path: CANDIDATE_ROUTES.DASHBOARD,
            show: true
        },
        {
            icon: (active: boolean) => <Sparkles size={20} strokeWidth={active ? 2.5 : 2} />,
            label: 'Talk to David',
            path: CANDIDATE_ROUTES.DASHBOARD + '?view=chat',
            show: true
        },
        {
            icon: (active: boolean) => <MessageSquare size={20} strokeWidth={active ? 2.5 : 2} />,
            label: 'Past chats',
            path: '/candidate/past-chats',
            show: false
        },
        {
            icon: (active: boolean) => <User size={20} strokeWidth={active ? 2.5 : 2} />,
            label: 'My Profile',
            path: CANDIDATE_ROUTES.MY_PROFILE,
            show: true
        },
        {
            icon: (active: boolean) => <Search size={20} strokeWidth={active ? 2.5 : 2} />,
            label: 'Browse Jobs',
            path: CANDIDATE_ROUTES.JOBS,
            show: true
        },
        {
            icon: (active: boolean) => <UploadCloud size={20} strokeWidth={active ? 2.5 : 2} />,
            label: 'Upload Resume',
            path: CANDIDATE_ROUTES.DASHBOARD + '?view=resume-upload',
            show: false
        },
        {
            icon: (active: boolean) => <ClipboardList size={20} strokeWidth={active ? 2.5 : 2} />,
            label: 'Applications',
            path: CANDIDATE_ROUTES.APPLICATIONS,
            badge: applicationsCount > 0 ? applicationsCount : null,
            show: true
        },
        {
            icon: (active: boolean) => <MessageSquare size={20} strokeWidth={active ? 2.5 : 2} />,
            label: 'Messages',
            path: CANDIDATE_ROUTES.MESSAGES,
            show: true
        },
        {
            icon: (active: boolean) => <Settings size={20} strokeWidth={active ? 2.5 : 2} />,
            label: 'Account Settings',
            path: SETTINGS_ROUTES.ACCOUNT_SETTINGS_CANDIDATE,
            show: true
        }
    ];

    return (
        <>
            <Sidebar
                isCollapsed={isCollapsed}
                onToggle={onToggle || (() => { })}
                isMobileOpen={isMobileOpen}
                onMobileClose={onMobileClose || (() => { })}
                menuItems={menuItems}
                onLogout={handleLogoutClick}
            />

            <ConfirmDialog
                isOpen={showLogoutDialog}
                variant="info"
                title="Log Out?"
                message="You'll be signed out of your Hush account on this device."
                confirmText="Log Out"
                cancelText="Cancel"
                onConfirm={handleLogoutConfirm}
                onCancel={handleLogoutCancel}
            />
        </>
    );
};

export default CandidateSidebar;
